export interface IndexedChat<T> {
  chat: T;
  index: number;
}

export const takeChatsForOptimisticDeletion = <T extends { id: string }>(
  chats: T[],
  chatIds: Iterable<string>
) => {
  const ids = new Set(chatIds);
  const removed: Array<IndexedChat<T>> = [];
  const remaining: T[] = [];

  chats.forEach((chat, index) => {
    if (ids.has(chat.id)) {
      removed.push({ chat, index });
      return;
    }
    remaining.push(chat);
  });

  return { remaining, removed };
};

export const restoreChatsAfterFailedDeletion = <T extends { id: string }>(
  current: T[],
  removed: Array<IndexedChat<T>>
) => {
  const restored = [...current];
  const presentIds = new Set(current.map((chat) => chat.id));

  [...removed]
    .sort((left, right) => left.index - right.index)
    .forEach(({ chat, index }) => {
      if (presentIds.has(chat.id)) return;
      restored.splice(Math.min(index, restored.length), 0, chat);
      presentIds.add(chat.id);
    });

  return restored;
};

export const getChatDeletionAnnouncement = (deletedCount: number, failedCount: number) => {
  if (failedCount === 0) {
    return deletedCount === 1
      ? 'Chat eliminado.'
      : `${deletedCount} chats eliminados.`;
  }

  if (deletedCount === 0) {
    return failedCount === 1
      ? 'No se pudo eliminar el chat. Se ha restaurado en el historial.'
      : `No se pudieron eliminar ${failedCount} chats. Se han restaurado en el historial.`;
  }

  const deletedText = deletedCount === 1 ? '1 chat eliminado.' : `${deletedCount} chats eliminados.`;
  const failedText = failedCount === 1
    ? '1 chat no se pudo eliminar y se ha restaurado.'
    : `${failedCount} chats no se pudieron eliminar y se han restaurado.`;
  return `${deletedText} ${failedText}`;
};
